import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { deleteVault, getDeleteVaultMutationOptions, getListVaultsQueryKey, type Vault } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { syncSecrets } from '@/modules/secrets/services/actions/sync';

// Takes the whole vault rather than its id so the success message can name it. Mutations bypass the
// shared query cache's `onError`, so this reports both outcomes through the messages module itself.
export const useDeleteVault = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: getDeleteVaultMutationOptions().mutationKey,
        mutationFn: (vault: Vault) => deleteVault({ vaultId: vault.id }),
        onSuccess: async (_, vault) => {
            // The sidebar and the settings table read the same list, so one invalidation refreshes both.
            await queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });
            // The secrets keep the dropped vault id in the store until the next periodic sync otherwise.
            dispatch(syncSecrets());

            dispatch(
                messagesActions.displaySuccessMessage({
                    message: { id: 'vaults.table.actions.delete.success', values: { vault: vault.name } },
                    options: { duration: 5 },
                }),
            );
        },
        onError: () =>
            dispatch(
                messagesActions.displayErrorMessage({
                    message: { id: 'vaults.table.actions.delete.error' },
                    options: { duration: 5 },
                }),
            ),
    });
};
